import React, { memo } from "react";
import { createPortal } from "react-dom";

import GridColumn, { GridValueType } from "../../components/grid/GridColumn";
import GridCell from "../../components/grid/GridCell";
import PageSpecification from "../../types/system/PageSpecification";
import Utilities from "../../foundation/Utility";
import Checkbox from "../inputform/Checkbox";

interface GridBodyRowProps<T> {
    columns: GridColumn<T>[];
    record: T;

    draggable?: boolean;
    dragContainerRef?: React.RefObject<HTMLTableSectionElement>;
    globalSelect: boolean;
    index: number;
    pageSpecification: PageSpecification;
    selected: boolean;
    selectedColumn: GridColumn<T>;
    singleSelected: boolean;
    bulkEdit: boolean;

    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
    onFocus?: (record: T, column: GridColumn<T>) => void;
    onBlur?: (record: T, column: GridColumn<T>) => void;
    onCheck?: (record: T, value: boolean) => void;
    onClick?: (record: T, column: GridColumn<T>) => void;
    onDoubleClick?: (record: T, column: GridColumn<T>) => void;
    onDragStart: (
        record: T,
        column: GridColumn<T>,
        event: React.MouseEvent,
        cell: React.RefObject<HTMLTableCellElement>,
    ) => void;
    valueEdited?: (record: T, column: GridColumn<T>, value: any) => void;
    prevRecordRequested?: () => void;
    nextRecordRequested?: () => void;
    prevColumnRequested?: () => void;
    nextColumnRequested?: () => void;
}

interface GridBodyRowState {
    ref: React.RefObject<HTMLTableRowElement>;
}

class GridBodyRow<T> extends React.Component<
    GridBodyRowProps<T>,
    GridBodyRowState
> {
    state: GridBodyRowState = {
        ref: React.createRef<HTMLTableRowElement>(),
    };

    componentDidMount(): void {}

    cellSelected(record: T, column: GridColumn<T>) {
        return (
            this.props.selected &&
            (this.props.selectedColumn?.field ?? "") === column.field
        );
    }

    cellSingleSelected(record: T, column: GridColumn<T>) {
        return (
            this.props.singleSelected &&
            (this.props.selectedColumn?.field ?? "") === column.field
        );
    }

    getFieldValue(
        index: number,
        record: T,
        field: string,
        value?: GridValueType,
    ): any {
        if (!!field)
            switch (field) {
                default:
                    return Utilities.getPathToValue(field, record);
            }
        if (!!value)
            switch (value) {
                case "[RowNo]":
                    return String(index);
            }
        return null;
    }

    getCellVisible(column: GridColumn<T>): boolean {
        if (column.visibleField) {
            const visible = Utilities.getPathToValue(
                column.visibleField,
                this.props.record,
            );
            if (typeof visible === "boolean") return visible;
        }
        return column.visible ?? true;
    }

    render(): React.ReactNode {
        const row = (
            <tr
                ref={this.state.ref}
                className={this.props.selected ? "selected" : ""}>
                {this.props.bulkEdit ? (
                    <td>
                        <Checkbox
                            value={this.props.selected}
                            onChange={value =>
                                this.props.onCheck?.(this.props.record, value)
                            }
                        />
                    </td>
                ) : null}
                {this.props.columns.map((column, index) => {
                    return this.getCellVisible(column) ? (
                        <GridCell
                            key={index}
                            column={column}
                            record={this.props.record}
                            value={this.getFieldValue(
                                this.props.index + 1,
                                this.props.record,
                                column.field,
                                column.value,
                            )}
                            draggable={this.props.draggable}
                            selected={this.cellSelected(
                                this.props.record,
                                column,
                            )}
                            globalSelect={this.props.globalSelect}
                            singleSelected={this.cellSingleSelected(
                                this.props.record,
                                column,
                            )}
                            pageSpecification={this.props.pageSpecification}
                            icon={column.icon}
                            onKeyDown={this.props.onKeyDown}
                            onKeyUp={this.props.onKeyUp}
                            onFocus={this.props.onFocus}
                            onBlur={this.props.onBlur}
                            onClick={this.props.onClick}
                            onDoubleClick={this.props.onDoubleClick}
                            onDragStart={this.props.onDragStart}
                            valueEdited={this.props.valueEdited}
                            prevRecordRequested={this.props.prevRecordRequested}
                            nextRecordRequested={this.props.nextRecordRequested}
                            prevColumnRequested={this.props.prevColumnRequested}
                            nextColumnRequested={this.props.nextColumnRequested}
                        />
                    ) : null;
                })}
            </tr>
        );
        return this.props.dragContainerRef?.current && this.props.selected
            ? createPortal(row, this.props.dragContainerRef.current)
            : row;
    }
}

export default memo(GridBodyRow);
